import { useMemo } from "react";
import { matchPath } from "react-router-dom";
import {
  addMonths,
  endOfDay,
  isWithinInterval,
  startOfDay,
  isValid
} from "date-fns";

import { hostRoutes, studentRoutes, TMenuRoute } from "routes";
import {
  BECOME_HOST_ROUTE,
  BLOG_ROUTE,
  CONTACT_ROUTE,
  FIND_ROOM_ROUTE,
  HOST_CHATS_ROUTE_ID,
  HOST_ROOMS_ROUTE,
  HOST_TENANTS_ROUTE,
  HOST_TIPS_ROUTE,
  MatchStatusEnum,
  PREFERABLE_TERMINATION_PERIOD_MONTHS_NUMBER,
  SHOWN_MATCHES_FROM_STATUS,
  STUDENT_CHATS_ROUTE_ID,
  STUDENT_MATCHES_ROUTE,
  STUDENT_ROOMS_ROUTE,
  STUDENT_SEARCHES_ROUTE,
  STUDENT_TIPS_ROUTE
} from "utils/constants";
import { useContextHospi } from "context/ContextHospi";
import { ThumbUpIcon } from "../components/Icons/ThumbUp";
import { useChat } from "../context/ContextChat/ContextChat";
import { useGetHostMatches } from "../API/queries/hostApiQueries";
import { defineStudentRentDate, isoDate } from "./helpers";
import { useGetStudentMatches } from "../API/queries/studentApiQueries";
import {
  DepositStatusEnum,
  TMatchData,
  TReview
} from "../interfaces/interfaces";
import { useGetReviewsByStudent } from "../API/queries/userApiQueries";
import { noEndDate } from "./time";

const studentMenuRoutes = [
  STUDENT_ROOMS_ROUTE,
  STUDENT_SEARCHES_ROUTE,
  STUDENT_CHATS_ROUTE_ID,
  STUDENT_MATCHES_ROUTE
];

const hostMenuRoutes = [
  HOST_CHATS_ROUTE_ID,
  HOST_TENANTS_ROUTE,
  HOST_ROOMS_ROUTE
];

export const useCountUnreadChats = (): number => {
  const { currentUserRole } = useContextHospi();
  const {
    state: { chatCounters }
  } = useChat();

  return useMemo(
    () =>
      currentUserRole === "host"
        ? (!!chatCounters?.unread_requested && chatCounters.unread_requested > 0
            ? chatCounters.unread_requested
            : 0) +
          (!!chatCounters?.unread_accepted && chatCounters.unread_accepted > 0
            ? chatCounters.unread_accepted
            : 0)
        : !!chatCounters?.unread_accepted && chatCounters.unread_accepted > 0
        ? chatCounters.unread_accepted
        : 0,
    [chatCounters, currentUserRole]
  );
};

export const useGetHelpNavbarRoutes = (): TMenuRoute[] => {
  const { currentUserRole } = useContextHospi();

  if (!currentUserRole) return [];
  return [
    {
      path: BECOME_HOST_ROUTE,
      keyLocalize: "hosting"
    },
    {
      path: FIND_ROOM_ROUTE,
      keyLocalize: "findingHousing"
    },
    {
      path: currentUserRole === "host" ? HOST_TIPS_ROUTE : STUDENT_TIPS_ROUTE,
      keyLocalize: "faq"
    },
    { path: CONTACT_ROUTE, keyLocalize: "contact" }
  ];
};

export const useGetProfileMenuRoutes = (): TMenuRoute[] => {
  const { currentUserRole, userProfile } = useContextHospi();

  if (!currentUserRole) return [];
  let menuRoutesData: TMenuRoute[] = [];
  if (userProfile) {
    (currentUserRole === "host" ? hostMenuRoutes : studentMenuRoutes).forEach(
      (r) => {
        const routeData: TMenuRoute | undefined = (
          currentUserRole === "host" ? hostRoutes : studentRoutes
        ).find((navRoute) => {
          return Boolean(matchPath(r, navRoute.path));
        });
        if (routeData) menuRoutesData.push(routeData);
      }
    );
  }

  return menuRoutesData.concat({
    keyLocalize: "blog",
    icon: <ThumbUpIcon />,
    path: BLOG_ROUTE
  } as TMenuRoute);
};

export const isChatPath = (path: string): boolean => {
  return (
    !!matchPath(path, STUDENT_CHATS_ROUTE_ID) ||
    !!matchPath(path, HOST_CHATS_ROUTE_ID)
  );
};

export const useCountMatchesActions = (): number => {
  const { currentUserRole, currentUserId, userProfile } = useContextHospi();
  const { data: hostMatches } = useGetHostMatches(
    currentUserId,
    currentUserRole === "host",
    (matches: TMatchData[]) =>
      matches.filter(
        (m) =>
          !!m.match_status &&
          m.match_status >= SHOWN_MATCHES_FROM_STATUS &&
          !(
            m.match_status === MatchStatusEnum.ARCHIVED &&
            !!m.most_recent_cancel_reason
          )
      )
  );
  const { data: studentMatches } = useGetStudentMatches(
    currentUserId,
    currentUserRole === "student",
    (matches: TMatchData[]) =>
      matches.filter(
        (m) => !!m.match_status && m.match_status >= SHOWN_MATCHES_FROM_STATUS
      )
  );

  const { data: reviews } = useGetReviewsByStudent(
    currentUserId,
    currentUserRole === "student" &&
      !!studentMatches?.length &&
      !!studentMatches.find((m) => {
        const start = defineStudentRentDate(
          m.check_in_date,
          userProfile?.preferred_move_in
        );
        return !!start?.length && new Date() > startOfDay(new Date(start));
      })
  );

  return useMemo(() => {
    const todayDate = new Date();

    if (currentUserRole === "host") {
      return hostMatches?.length
        ? hostMatches.reduce((acc, m) => {
            let matchActionsNumber = 0;
            const notPastMatch =
              m.match_status !== MatchStatusEnum.ARCHIVED &&
              (!m.check_out_date?.length ||
                !isValid(new Date(m.check_out_date)) ||
                !m.check_in_date?.length ||
                !isValid(new Date(m.check_in_date)) ||
                m.check_out_date >= isoDate(todayDate));

            if (
              (m.contract_status === "draft" ||
                (m.contract_status === "finished" &&
                  !m.host_action_extras?.is_finished_contract_checked)) &&
              notPastMatch
            ) {
              // Match "Contract" category in HOST_TENANTS_ROUTE UI
              matchActionsNumber = ++matchActionsNumber;
            }
            if (
              notPastMatch &&
              !m.host_action_extras?.is_paid_deposit_checked &&
              m.deposit_status === DepositStatusEnum.PAYED
            ) {
              // Match "Deposit" category in HOST_TENANTS_ROUTE UI
              matchActionsNumber = ++matchActionsNumber;
            }

            const checkInDate: false | Date =
              !!m.check_in_date && new Date(m.check_in_date);
            if (
              (notPastMatch &&
                !m.host_action_extras?.is_9month_termination_checked &&
                (!m.check_out_date || noEndDate(new Date(m.check_out_date))) &&
                !!checkInDate &&
                isWithinInterval(todayDate, {
                  start: startOfDay(checkInDate),
                  end: endOfDay(
                    addMonths(
                      checkInDate,
                      PREFERABLE_TERMINATION_PERIOD_MONTHS_NUMBER
                    )
                  )
                })) ||
              (m.checkout_date_actor === "student" &&
                !m.host_action_extras?.is_tenant_ended_rental_period_checked)
            ) {
              // Match "Rental period" category in HOST_TENANTS_ROUTE UI
              matchActionsNumber = ++matchActionsNumber;
            }

            return matchActionsNumber ? acc + matchActionsNumber : acc;
          }, 0)
        : 0;
    } else if (currentUserRole === "student") {
      return studentMatches?.length
        ? studentMatches.reduce((acc, m) => {
            const endDate = defineStudentRentDate(
              m.check_out_date,
              userProfile?.preferred_move_out
            );
            let matchActionsNumber = 0;

            if (
              m.contract_status === "host_signed" &&
              m.match_status !== MatchStatusEnum.ARCHIVED &&
              !(
                !!endDate?.length &&
                isValid(new Date(endDate)) &&
                new Date(endDate) < startOfDay(todayDate)
              )
            ) {
              // Match "Contract" category in STUDENT_MATCHES_ROUTE UI
              matchActionsNumber = ++matchActionsNumber;
            }
            if (m.payment_status !== "PAID" && !!m.payment_link?.length) {
              // Match "Payments" category in STUDENT_MATCHES_ROUTE UI
              matchActionsNumber = ++matchActionsNumber;
            }
            if (
              (m.deposit_status === DepositStatusEnum.DISPUTE_INITIATED &&
                !m.student_action_extras
                  ?.is_withhold_deposit_proposal_checked) ||
              (m.contract_status === "finished" &&
                m.match_status !== MatchStatusEnum.ARCHIVED &&
                !!m.check_out_date &&
                startOfDay(todayDate) <= new Date(m.check_out_date) &&
                (!m.deposit_id ||
                  m.deposit_status === DepositStatusEnum.FAILED ||
                  m.deposit_status === DepositStatusEnum.PENDING) &&
                !m.student_action_extras?.is_pay_deposit_checked &&
                !!m.action_feature_enabled)
            ) {
              // Match "Deposit" category in STUDENT_MATCHES_ROUTE UI
              // Currently, is_pay_deposit_checked action icon doesn't depend on deposit amount (therefore, doesn't take into account MINIMUM_EUR_STRIPE_CHARGABLE_AMOUNT)
              // as a contract deposit amount requires to be fetched from the BE using, for instance, useGetStudentAggregatedMatches instead of useGetStudentMatches
              matchActionsNumber = ++matchActionsNumber;
            }
            if (reviews) {
              // Match "Review" category in STUDENT_MATCHES_ROUTE UI
              const start = defineStudentRentDate(
                m.check_in_date,
                userProfile?.preferred_move_in
              );

              if (
                !!start?.length &&
                todayDate > startOfDay(new Date(start)) &&
                !reviews.find(
                  (r: TReview) =>
                    r.host_id === m.host_id && r.house_id === m.house_id
                ) &&
                !m.student_action_extras?.is_create_review_checked
              ) {
                matchActionsNumber = ++matchActionsNumber;
              }
            }
            if (
              m.checkout_date_actor === "host" &&
              !m.student_action_extras?.is_host_ended_rental_period_checked
            ) {
              // Match "Rental period" category in STUDENT_MATCHES_ROUTE UI
              matchActionsNumber = ++matchActionsNumber;
            }

            return matchActionsNumber ? acc + matchActionsNumber : acc;
          }, 0)
        : 0;
    } else return 0;
  }, [
    hostMatches,
    studentMatches,
    currentUserRole,
    userProfile?.preferred_move_out,
    reviews,
    userProfile?.preferred_move_in
  ]);
};
